function isLeakedListPaginationPayload(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const obj = value as Record<string, unknown>;
  return (
    typeof obj['totalCount'] === 'number' &&
    typeof obj['page'] === 'number' &&
    typeof obj['pageSize'] === 'number'
  );
}

function isLeakedStructuredEnvelope(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  const data = candidate['data'] as Record<string, unknown> | undefined;
  const meta = candidate['meta'];

  return (
    typeof candidate['answer'] === 'string' &&
    typeof candidate['type'] === 'string' &&
    typeof candidate['entity'] === 'string' &&
    Boolean(data) &&
    Array.isArray(data?.['items']) &&
    Boolean(meta) &&
    typeof meta === 'object' &&
    !Array.isArray(meta)
  );
}

function isLeakedAssistantJson(value: unknown): boolean {
  return isLeakedStructuredEnvelope(value) || isLeakedListPaginationPayload(value);
}

function looksLikeTruncatedListMetaJson(raw: string): boolean {
  const compact = raw.replace(/\s+/g, ' ');
  return (
    compact.includes('"totalCount"') && compact.includes('"pageSize"') && compact.includes('"page"')
  );
}

function stripLeakedJsonFencedBlocks(markdown: string): string {
  const pattern = /```(?:json)?\s*([\s\S]*?)```/gi;

  return markdown.replace(pattern, (full, inner: string) => {
    const trimmed = inner.trim();
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (isLeakedAssistantJson(parsed)) {
        return '';
      }
    } catch {
      if (looksLikeTruncatedListMetaJson(trimmed)) {
        return '';
      }
    }
    return full;
  });
}

function stripIncompleteJsonCodeFence(markdown: string): string {
  const openRe = /```(?:json)?\s*\n\s*\{/g;
  let lastIndex = -1;
  let match: RegExpExecArray | null;
  while ((match = openRe.exec(markdown)) !== null) {
    lastIndex = match.index;
  }
  if (lastIndex === -1) {
    return markdown;
  }

  const sliceFromFence = markdown.slice(lastIndex);
  const contentMatch = sliceFromFence.match(/^```(?:json)?\s*\n\s*(\{[\s\S]*)$/);
  if (!contentMatch) {
    return markdown;
  }

  const tailFromBrace = contentMatch[1];
  if (/\n```\s*$/.test(tailFromBrace) || tailFromBrace.includes('\n```')) {
    return markdown;
  }

  if (looksLikeTruncatedListMetaJson(tailFromBrace)) {
    return markdown.slice(0, lastIndex).trimEnd();
  }

  return markdown;
}

function stripTrailingUnfencedJsonLeak(markdown: string): string {
  const trimmed = markdown.trimEnd();

  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (isLeakedAssistantJson(parsed)) {
        return '';
      }
    } catch {
      if (looksLikeTruncatedListMetaJson(trimmed)) {
        return '';
      }
    }
    return markdown;
  }

  let searchEnd = trimmed.length;
  while (searchEnd > 0) {
    const idx = trimmed.lastIndexOf('\n{', searchEnd - 1);
    if (idx === -1) {
      break;
    }

    const suffix = trimmed.slice(idx + 1).trim();
    if (!suffix.startsWith('{')) {
      searchEnd = idx;
      continue;
    }

    try {
      const parsed = JSON.parse(suffix) as unknown;
      if (isLeakedAssistantJson(parsed)) {
        return trimmed.slice(0, idx).trimEnd();
      }
    } catch {
      if (looksLikeTruncatedListMetaJson(suffix)) {
        return trimmed.slice(0, idx).trimEnd();
      }
    }

    searchEnd = idx;
  }

  return markdown;
}

function collapseExtraBlankLines(text: string): string {
  return text.replace(/\n{3,}/g, '\n\n').trimEnd();
}

/**
 * Esconde JSON interno / metadados de lista que o modelo às vezes vaza no
 * markdown — o cliente renderiza apenas markdown puro, então qualquer
 * payload estruturado que escape para o texto é ruído.
 */
export function sanitizeAssistantChatContent(markdown: string): string {
  if (!markdown?.trim()) {
    return markdown;
  }

  let out = stripLeakedJsonFencedBlocks(markdown);
  out = stripIncompleteJsonCodeFence(out);
  out = stripTrailingUnfencedJsonLeak(out);
  out = collapseExtraBlankLines(out);
  return out.trimEnd();
}
